import {
  PALETTE,
  getMessageTypeFromPalette,
  isMessageWorkflowType,
  isWhatsappPalette
} from "../workflowPaletteConstants";
import { FRIENDLY_FIELD_LABELS } from "./workflowInspectorFieldUx";
import {
  SESSION_TIMEOUT_MAX_SECONDS,
  SESSION_TIMEOUT_MIN_SECONDS,
} from "../workflowFlowMapper";

/**
 * Validate a single workflow node for incomplete key-value pairs.
 * Checks both `saveToContext` and recursively checks `config` for any
 * array of `{key, value}` objects or plain objects used as key-value maps.
 * Returns an array of human-readable error strings (empty if valid).
 */
export function validateNodeKeyValues(nodeData) {
  const errors = [];
  const label = nodeData?.label || "Unknown";
  const isClearVariable = nodeData?.config?.action === "CLEAR_VARIABLE";

  const saveToContext = nodeData?.saveToContext;
  if (saveToContext) {
    if (Array.isArray(saveToContext)) {
      const hasIncomplete = saveToContext.some(row => !String(row.key || "").trim() || (!isClearVariable && !String(row.value || "").trim()));
      if (hasIncomplete) {
        errors.push(`Step "${label}" has incomplete Remembered Values (missing key or value).`);
      }
    } else if (typeof saveToContext === "object") {
      const hasIncomplete = Object.entries(saveToContext).some(([k, v]) => !String(k || "").trim() || (!isClearVariable && !String(v ?? "").trim()));
      if (hasIncomplete) {
        errors.push(`Step "${label}" has incomplete Remembered Values (missing key or value).`);
      }
    }
  }

  const checkConfig = (obj) => {
    if (!obj || typeof obj !== "object") return;
    Object.entries(obj).forEach(([key, val]) => {
      if (Array.isArray(val)) {
        const isKeyValueArray = val.every(
          (item) => item && typeof item === "object" && "key" in item && "value" in item
        );
        if (isKeyValueArray) {
          const hasIncomplete = val.some(row => !String(row.key || "").trim() || (!isClearVariable && !String(row.value || "").trim()));
          if (hasIncomplete) {
            const friendlyLabel = FRIENDLY_FIELD_LABELS[key] || key;
            errors.push(`Step "${label}" has incomplete ${friendlyLabel} (missing key or value).`);
          }
        }
      } else if (val && typeof val === "object") {
        if (key === "params" || key === "headers") {
          const hasIncomplete = Object.entries(val).some(([k, v]) => !String(k || "").trim() || !String(v ?? "").trim());
          if (hasIncomplete) {
            const friendlyLabel = FRIENDLY_FIELD_LABELS[key] || key;
            errors.push(`Step "${label}" has incomplete ${friendlyLabel} (missing key or value).`);
          }
        } else {
          checkConfig(val);
        }
      }
    });
  };
  checkConfig(nodeData?.config);

  const options = nodeData?.config?.message?.options;
  if (Array.isArray(options)) {
    const hasEmptyButton = options.some(opt => !String(opt.label || "").trim());
    if (hasEmptyButton) {
      errors.push(`Step "${label}" has one or more empty reply buttons.`);
    }
  }

  const items = nodeData?.config?.message?.items;
  if (Array.isArray(items)) {
    const hasEmptyItem = items.some(item => !String(item.label || "").trim());
    if (hasEmptyItem) {
      errors.push(`Step "${label}" has one or more empty menu options.`);
    }
  }

  return errors;
}

function isWorkflowMessageNode(nodeData) {
  const workflowType = nodeData?.workflowType;
  const paletteType = nodeData?.paletteType;
  if (paletteType === PALETTE.END || workflowType === "END") return false;
  return isMessageWorkflowType(workflowType) || isWhatsappPalette(paletteType);
}

/**
 * Validate WhatsApp message nodes (notify, await reply, send message) for required
 * message content. Uses config.message directly — not canvas preview (which falls back to label).
 */
export function validateNodeMessageContent(nodeData) {
  const errors = [];
  if (!isWorkflowMessageNode(nodeData)) return errors;

  const label = nodeData?.label || "Unknown";
  const message = nodeData?.config?.message;
  const messageType = message?.type || getMessageTypeFromPalette(nodeData?.paletteType) || "text";

  switch (messageType) {
    case "text":
    case "buttons":
    case "list":
      if (!String(message?.text ?? "").trim()) {
        errors.push(`Step "${label}" is missing message text.`);
      }
      break;
    case "image":
    case "video":
    case "document":
      if (!String(message?.url ?? "").trim() && !String(message?.mediaId ?? "").trim() && !message?.pendingFile) {
        errors.push(`Step "${label}" is missing media (upload a file or enter a URL).`);
      }
      break;
    case "template": {
      const templateRef = message?.name || message?.templateName || message?.templateId;
      if (!String(templateRef ?? "").trim()) {
        errors.push(`Step "${label}" is missing a WhatsApp template.`);
      }
      break;
    }
    default:
      if (!String(message?.text ?? "").trim()) {
        errors.push(`Step "${label}" is missing message content.`);
      }
  }

  return errors;
}

/**
 * Validate session timeout value to be within the allowed range.
 * Returns an error string if invalid, or null if valid.
 */
export function validateSessionTimeout(timeout) {
  const rangeMessage = `Session timeout must be between ${SESSION_TIMEOUT_MIN_SECONDS} and ${SESSION_TIMEOUT_MAX_SECONDS} seconds.`;
  if (timeout === "" || timeout === undefined || timeout === null) {
    return rangeMessage;
  }
  const parsed = parseInt(timeout, 10);
  if (
    Number.isNaN(parsed) ||
    parsed < SESSION_TIMEOUT_MIN_SECONDS ||
    parsed > SESSION_TIMEOUT_MAX_SECONDS
  ) {
    return rangeMessage;
  }
  return null;
}
